import { Link } from 'react-router-dom'
import {
  type MapIslandDto,
  type IncomingAttackDto,
  type IncomingPlayerRaidDto,
} from '@/api/hooks'
import { ReconComposition, ReconRepel } from '@/components/ReconIntel'
import { LanternIcon,
  AnchorIcon, CloseIcon, ScopeIcon, PennantIcon,
} from '@/components/game/GameIcons'

import {
  islandTypeLabel,
  FactionBadge,
} from '@/components/game/map/mapMarkers'
import { CaptainPortrait } from '@/components/game/cosmetics/CaptainPortrait'
import { CaptainBlason } from '@/components/game/cosmetics/CaptainBlason'
import { PanelSection, VoyageRiskNote } from '@/components/game/map/launcherBits'
import { TradeSection } from '@/components/game/map/launchers/TradeLaunchers'
import { RaidLauncher } from '@/components/game/map/launchers/RaidLauncher'
import { SalvageLauncher } from '@/components/game/map/launchers/SalvageLauncher'
import { ScoutLauncher } from '@/components/game/map/launchers/ScoutLauncher'
import { PvpRaidLauncher } from '@/components/game/map/launchers/PvpRaidLauncher'
import { OutlawSection } from '@/components/game/map/launchers/OutlawLaunchers'
import { ExploreLauncher, MysteriousExpeditionPanel } from '@/components/game/map/launchers/ExpeditionLauncher'
import { attackColor, cardinalDirection, IncomingEta, IncomingPvpRaidReadout } from '@/components/game/map/attackLayers'
import { TRADE_DESTINATION_TYPES, SMUGGLE_DESTINATION_TYPES, isMarineWarCitadel } from '@/components/game/map/mapConstants'

/**
 * Le Grand Phare d'un rival, tel qu'on le voit de l'extérieur : combien de paliers
 * se dressent, rien d'autre. Ni le devis, ni la voie prise, ni la pierre en
 * réserve : montrer l'ouvrage suffit à transformer un sink de ressources en course,
 * l'exposer entièrement donnerait un avantage d'information.
 */
function ChantierBadge({ tiers }: { tiers: number }) {
  if (tiers <= 0) return null

  return (
    <p className="flex items-center gap-1.5 text-sm">
      <LanternIcon className="w-4 h-4 text-sand-300/80 shrink-0" />
      <span className="text-sand-200">
        Grand Phare : <strong className="text-sand-300">{tiers}</strong> palier{tiers > 1 ? 's' : ''} sur 5
      </span>
    </p>
  )
}

export function SidePanel({
  selected,
  onCleared,
  incomingAttack,
  incomingPlayerRaids = [],
  variant = 'panel',
}: {
  selected: MapIslandDto | null
  onCleared: () => void
  incomingAttack?: IncomingAttackDto | null
  incomingPlayerRaids?: IncomingPlayerRaidDto[]
  variant?: 'panel' | 'sheet'
}) {
  if (!selected) {
    return (
      <div className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-5 text-sm text-ocean-300 italic">
        Clique une île pour la détailler.
      </div>
    )
  }

  // A foreign captain's island — the PvP target. Distinct from NPC lairs (no
  // commerce/smuggle here) and from the player's own island.
  const foreignPlayer = !selected.isMine && !selected.isNpc

  // In 'sheet' the bottom sheet provides the rounded card chrome, so the panel
  // body stays flat; 'panel' keeps the standalone card look.
  const shell =
    variant === 'sheet'
      ? 'p-2 space-y-3'
      : 'rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-5 space-y-3'

  return (
    <div className={shell}>
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-display text-xl text-sand-300">{selected.name}</h3>
          <p className="text-sm text-ocean-200">{islandTypeLabel(selected)}</p>
        </div>
        <button
          onClick={onCleared}
          className="text-ocean-300 hover:text-ocean-100 transition-colors"
          aria-label="Fermer"
        >
          <CloseIcon className="w-4 h-4" />
        </button>
      </div>
      {/* Crossing-event risk — surfaced here, where the captain commits to a real
          voyage (any away target), NOT in the first-patrol onboarding card. Skipped
          for the player's own island (no crossing from home). */}
      {!selected.isMine && <VoyageRiskNote />}
      <div className="text-sm text-ocean-100 space-y-1">
        <p>Position : ({selected.x}, {selected.y})</p>
        {selected.isMine && <p className="text-green-300 flex items-center gap-1.5"><AnchorIcon className="w-4 h-4" /> Ton repaire</p>}
        {/* Foreign player island: owner pseudo + the emergent-faction pennant (pavillon + rang).
            Un capitaine passé par la cabine affiche en plus son identité cosmétique :
            portrait à gauche du pseudo, blason à droite, titre équipé sous le pseudo.
            Le FactionBadge reste dans tous les cas (la faction est une info de gameplay). */}
        {foreignPlayer && (
          <>
            {selected.ownerAppearance ? (
              <div className="flex items-center gap-3 py-1">
                <CaptainPortrait config={selected.ownerAppearance.avatar} size={48} className="shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1.5">
                    <PennantIcon className="w-4 h-4 text-sand-300/80 shrink-0" />
                    <span className="text-sand-300 truncate">{selected.ownerPseudo}</span>
                  </p>
                  {selected.ownerAppearance.titleLabel && (
                    <p className="text-xs text-sand-200 italic truncate">{selected.ownerAppearance.titleLabel}</p>
                  )}
                </div>
                <CaptainBlason config={selected.ownerAppearance.blason} size={32} className="shrink-0" />
              </div>
            ) : (
              selected.ownerPseudo && (
                <p className="flex items-center gap-1.5">
                  <PennantIcon className="w-4 h-4 text-sand-300/80" /> <span className="text-sand-300">{selected.ownerPseudo}</span>
                </p>
              )
            )}
            <p><FactionBadge faction={selected.ownerFaction} rank={selected.ownerRank} /></p>
            <ChantierBadge tiers={selected.ownerChantierTiers ?? 0} />
          </>
        )}
        {selected.ownerPseudo && !selected.isMine && !foreignPlayer && (
          <p>Pavillon : <span className="text-sand-300">{selected.ownerPseudo}</span></p>
        )}
      </div>

      {selected.isMine && incomingAttack && (() => {
        const c = attackColor(incomingAttack.faction)
        // Watchtower-gated recon — only surfaced if the map payload carries it
        // (forward-compatible; `/api/map` doesn't expose the tiers yet).
        const showLocked = incomingAttack.moreIntelIfUpgraded ?? false
        // Provenance : l'origine est un point d'HORIZON sur le bord de carte (cf.
        // RaidOrigin serveur — l'ennemi surgit du large, pas d'un repaire nommé).
        // On en donne donc la DIRECTION, pas un nom d'île. `selected` EST mon île
        // ici (selected.isMine), d'où le calcul du cap depuis chez moi.
        const direction = cardinalDirection(selected, incomingAttack.origin)
        return (
          <div className="rounded-xl p-3 text-sm space-y-2" style={{ background: `${c}1a`, border: `1px solid ${c}66` }}>
            <p className="flex items-center gap-1.5" style={{ color: c }}>
              <span aria-hidden="true">{incomingAttack.faction.emoji}</span>
              <strong>{incomingAttack.faction.label}</strong> en approche
            </p>
            {/* Petite explication de ce qu'est cette attaque (le joueur arrive ici
                en cliquant le tracé sur la carte). */}
            <p className="text-xs text-ocean-200 leading-relaxed">
              Une flotte hostile fait voile vers ton île. À son arrivée, elle se heurte à tes défenses
              (Fort, Batterie, Milice). Ta <strong className="text-sand-200">flotte à quai ne participe pas
              à la défense</strong> : elle est prise par surprise et peut être pillée (pertes plafonnées).
              Renforce tes défenses depuis{' '}
              <Link
                to="/island#defense"
                className="text-sand-300 underline decoration-dotted underline-offset-2 hover:text-sand-100 transition-colors"
              >
                Mon île
              </Link>
              .
            </p>
            <IncomingEta attack={incomingAttack} />
            <p className="flex items-center gap-1.5 text-ocean-200">
              <span className="text-ocean-300">Provenance</span>
              <span className="text-sand-200">l'horizon, côté {direction}</span>
            </p>
            <ReconComposition
              composition={incomingAttack.composition}
              showLockedHint={showLocked}
              requiredLevel={incomingAttack.intelTiers?.composition}
            />
            <ReconRepel
              estimate={incomingAttack.repelEstimate}
              showLockedHint={showLocked}
              requiredLevel={incomingAttack.intelTiers?.repel}
            />
          </div>
        )
      })()}
      {/* Incoming PvP raids (Watchtower-gated) — one readout per human attacker,
          the line twins live in the map overlay (IncomingPvpRaidLayer). */}
      {selected.isMine && incomingPlayerRaids.length > 0 && (
        <div className="space-y-2">
          {incomingPlayerRaids.map((raid, i) => (
            <IncomingPvpRaidReadout
              key={`${raid.attackerPseudo ?? 'inconnu'}-${raid.origin.x}-${raid.origin.y}-${i}`}
              raid={raid}
            />
          ))}
        </div>
      )}
      {selected.isMine && (
        <p className="text-xs text-ocean-300 italic">
          Gère ressources et bâtiments depuis la page <strong>Mon île</strong>.
        </p>
      )}
      {/* Generic exploration charts an unknown NPC island — EXCEPT a mysterious
          one, which is expedition-only (it charts itself at launch). */}
      {!selected.isDiscovered && selected.isNpc && selected.type !== 'mysterious' && (
        <ExploreLauncher island={selected} />
      )}
      {/* Overlapping options ordered safest → riskiest: commerce → contrebande →
          raid. A pirate lair drops legal commerce (no honest trade with cutthroats),
          keeping raid + contrebande. A mysterious island offers NONE of these — it's a
          narrative-only site: you send an EXPEDITION (a fleet) and read its journal.
          The panel is a small state machine over the active expedition (if any). */}
      {selected.type === 'mysterious' && (
        <PanelSection label="Expédition">
          <MysteriousExpeditionPanel island={selected} />
        </PanelSection>
      )}
      {/* Apex Marine harbors are war citadels — raid target only, no honest trade
          (mirrors the backend TradeRunService guard). Anchor ports still trade. */}
      {selected.isDiscovered && TRADE_DESTINATION_TYPES.includes(selected.type) && !isMarineWarCitadel(selected) && (
        <PanelSection label="Commerce">
          <TradeSection destination={selected} />
        </PanelSection>
      )}
      {selected.isDiscovered && SMUGGLE_DESTINATION_TYPES.includes(selected.type) && (
        <PanelSection label="Contrebande">
          <OutlawSection island={selected} />
        </PanelSection>
      )}
      {selected.isDiscovered && selected.isNpc && selected.raidable && (
        <PanelSection label="Raid">
          <RaidLauncher island={selected} />
        </PanelSection>
      )}

      {/* Foreign player island — the PvP target. Danger is gated behind a scout:
          unscouted shows only the "send an éclaireur" prompt (no numbers), scouted
          reveals the threat readout via PvpRaidLauncher. The raid launcher renders
          regardless of raidable so it can show WHY it's blocked. */}
      {foreignPlayer && (
        <>
          {!selected.scouted && (
            <p className="text-xs text-ocean-300 italic flex items-center gap-1.5">
              <ScopeIcon className="w-4 h-4 text-ocean-300/70" /> Défenses inconnues : envoyer un éclaireur.
            </p>
          )}
          <PanelSection label="Éclaireur">
            <ScoutLauncher island={selected} />
          </PanelSection>
          <PanelSection label="Raid">
            <PvpRaidLauncher island={selected} />
          </PanelSection>
        </>
      )}

      {selected.isDiscovered && selected.type === 'recif' && (
        <PanelSection label="Récupération">
          <SalvageLauncher island={selected} />
        </PanelSection>
      )}
    </div>
  )
}
